import { Node, mergeAttributes } from '@tiptap/core';
import { Node as PmNode } from '@tiptap/pm/model';
import { Plugin } from '@tiptap/pm/state';

function isColumnEffectivelyEmpty(column: PmNode): boolean {
  if (column.content.size === 0) return true;
  if (
    column.childCount === 1 &&
    column.firstChild?.type.name === 'paragraph' &&
    column.firstChild.content.size === 0
  ) {
    return true;
  }
  return false;
}

const ColumnLayout = Node.create({
  name: 'columnLayout',
  group: 'block',
  content: 'column column',
  draggable: false,
  isolating: true,
  defining: true,

  parseHTML() {
    return [{ tag: 'div.column-layout' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes({ class: 'column-layout' }, HTMLAttributes),
      0,
    ];
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        appendTransaction(_transactions, _oldState, newState) {
          const { tr } = newState;
          let modified = false;

          // Iterate in reverse to handle position shifts correctly
          const layoutPositions: { pos: number; node: PmNode }[] = [];
          newState.doc.descendants((node, pos) => {
            if (node.type.name === 'columnLayout') {
              layoutPositions.push({ pos, node });
              return false; // Don't descend into column layouts
            }
          });

          // Process in reverse document order
          for (let i = layoutPositions.length - 1; i >= 0; i--) {
            const { pos, node } = layoutPositions[i];

            // Ensure exactly 2 children (columns)
            if (node.childCount !== 2) continue;

            const firstColumn = node.child(0);
            const secondColumn = node.child(1);

            const firstEmpty = isColumnEffectivelyEmpty(firstColumn);
            const secondEmpty = isColumnEffectivelyEmpty(secondColumn);

            if (firstEmpty && secondEmpty) {
              // Both empty: replace with a single empty paragraph
              const paragraph =
                newState.schema.nodes.paragraph.create();
              tr.replaceWith(pos, pos + node.nodeSize, paragraph);
              modified = true;
            } else if (firstEmpty) {
              // Unwrap second column's content
              tr.replaceWith(pos, pos + node.nodeSize, secondColumn.content);
              modified = true;
            } else if (secondEmpty) {
              // Unwrap first column's content
              tr.replaceWith(pos, pos + node.nodeSize, firstColumn.content);
              modified = true;
            }

            // Check for nested columnLayout inside columns
            if (!firstEmpty && !secondEmpty) {
              let hasNestedLayout = false;
              node.descendants((child) => {
                if (child.type.name === 'columnLayout') {
                  hasNestedLayout = true;
                  return false;
                }
              });

              if (hasNestedLayout) {
                // Flatten: unwrap the entire layout into its constituent blocks
                const blocks: PmNode[] = [];
                node.forEach((column) => {
                  column.forEach((block) => {
                    blocks.push(block);
                  });
                });
                tr.replaceWith(
                  pos,
                  pos + node.nodeSize,
                  blocks,
                );
                modified = true;
              }
            }
          }

          return modified ? tr : null;
        },
      }),
    ];
  },
});

export default ColumnLayout;
